/**
 * GoogleTagManager class extends TrackingScript class.
 * This class is responsible for managing Google Tag Manager related operations.
 */
import TrackingScript from './tracking-script';
import { appendRecruitmentLocationToPageData, resolveRecruitmentPageGroupForGtm } from './delco-tracking';
import { getActivatedExperiments } from '../ab-tests/feature-management';
import { cookiesAccepted } from '../cookies';
import { getUserId } from '../user-id-provider';
import { usesModelTracking } from './delivery-model-tracking';
import { v4 as uuidv4 } from 'uuid';

// Legacy events Dual model Employee + IC countries keep: the client manages the cookies with hasGivenCookieConsent,
// and hasChangedLanguage counts the language switches in the header
const MODEL_KEPT_LEGACY_EVENTS = ['hasGivenCookieConsent', 'hasChangedLanguage'];

/**
 * Dual model Employee + IC countries only send the Dual model Employee + IC events, apart from MODEL_KEPT_LEGACY_EVENTS.
 * @param {string} eventName - The name of the legacy event.
 * @returns {boolean}
 */
function skipsLegacyEvent(eventName) {
    return usesModelTracking() && !MODEL_KEPT_LEGACY_EVENTS.includes(eventName);
}

export default class GoogleTagManager extends TrackingScript {
    //Queue for tracking events that were called before the new cookie wall was accepted
    preCookieAcceptQueue = [];
    //The default pageData
    pageData = {};
    //Boolean to indicate if page data has been set
    hasSetPageData = false;
    //Boolean to detect if Pageview with '_inactive_city' was fired (have to remove '_inactive_city' for the first one)
    seenPageViewInactiveCity = false;
    //Track original referrer and location on first page load
    originalReferrer = null;
    originalLocation = null;

    /**
     * Constructor for GoogleTagManager class.
     * It calls the inject method and sets the page data.
     */
    constructor() {
        super();
        if (!window.gtmTrackingEnabled) {
            return;
        }
        this.needsCookies = false;

        // Capture original referrer and location on initialization
        this.captureOriginalData();

        this.inject().then(() => {
            this.sendEventsAfterCookieConsent();
        });
    }

    /**
     * Capture original referrer and location data that persists across page transitions
     */
    captureOriginalData() {
        if (!this.originalReferrer && !this.originalLocation) {
            this.originalReferrer = document.referrer || null;
            this.originalLocation = window.location.href;

            // Store in sessionStorage to persist across traditional page loads
            try {
                if (!sessionStorage.getItem('original_referrer')) {
                    sessionStorage.setItem('original_referrer', this.originalReferrer || '');
                }
                if (!sessionStorage.getItem('original_location')) {
                    sessionStorage.setItem('original_location', this.originalLocation);
                }
            } catch (e) {
                if (process.env.NODE_ENV !== 'production') {
                    console.log('tracking event error:', e.message); // eslint-disable-line no-console
                }
            }
        } else {
            // Retrieve from storage if available
            try {
                this.originalReferrer = sessionStorage.getItem('original_referrer') || null;
                this.originalLocation = sessionStorage.getItem('original_location') || window.location.href;
            } catch (e) {
                if (process.env.NODE_ENV !== 'production') {
                    console.log('tracking event error:', e.message); // eslint-disable-line no-console
                }
            }
        }
    }

    /**
     * This method checks if the Google Tag Manager script can be injected and if it can, it injects the script and noscript tags.
     */
    async inject() {
        this.addScript();
        this.addNoScript();
    }

    /**
     * All GTM containers to load, in DOM order: the primary (default or per-language override),
     * the per-language additional container (loaded alongside, not instead), and the internal one.
     * @returns {string[]} The unique container ids.
     */
    gtmContainerIds() {
        return [
            ...new Set(
                [
                    window.gtmContainerId,
                    window.gtmAdditionalContainerId,
                    window.gtmAddOwnContainer ? 'GTM-N2M9WJS9' : null,
                ].filter(Boolean),
            ),
        ];
    }

    /**
     * This method builds the Google Tag Manager bootstrap snippet for a container.
     * @param {string} containerId - The GTM container id.
     * @returns {string} The snippet source.
     */
    buildGtmSnippet(containerId) {
        return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${containerId}');`;
    }

    /**
     * This method creates and injects a Google Tag Manager script tag per container into the head of the document.
     */
    addScript() {
        let previousScript = null;

        this.gtmContainerIds().forEach((containerId) => {
            const script = document.createElement('script');
            script.textContent = this.buildGtmSnippet(containerId);
            document.head.insertBefore(script, previousScript ? previousScript.nextSibling : document.head.firstChild);
            previousScript = script;
        });
    }

    /**
     * This method creates and injects a noscript tag into the body with an iframe per container.
     */
    addNoScript() {
        //Append a noscript tag to the body
        let noscript = document.createElement('noscript');

        noscript.innerHTML = this.gtmContainerIds()
            .map(
                (containerId) =>
                    `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            )
            .join('');
        document.body.insertBefore(noscript, document.body.firstChild);
    }

    /**
     * This method sends events after the cookie consent has been given.
     */
    sendEventsAfterCookieConsent() {
        let eventInterval = setInterval(() => {
            if (this.hasCookieConsentForNewBanner()) {
                clearInterval(eventInterval);
                if (this.preCookieAcceptQueue.length) {
                    while (this.preCookieAcceptQueue.length) {
                        const nextEvent = this.preCookieAcceptQueue.shift();
                        this.executeTrackingEvent(nextEvent);
                    }
                }
            }
        }, 2000);
    }

    /**
     * This method executes a tracking event.
     * @param {Function} eventFunction - The tracking event function to be executed.
     */
    executeTrackingEvent(eventFunction) {
        //check if cookie has been accepted
        if (!this.hasCookieConsentForNewBanner()) {
            this.preCookieAcceptQueue.push(eventFunction);
            return;
        }
        try {
            eventFunction();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.log('tracking event error:', err.message); // eslint-disable-line no-console
            }
        }
    }

    /**
     * This method tracks a legacy event with a given name. Dual model Employee + IC countries only send the Dual model Employee + IC events,
     * so the legacy events are skipped there (see skipsLegacyEvent()).
     * @param {string} eventName - The name of the event to be tracked.
     * @param {Object} options - The options for the event tracking, see pushEvent().
     */
    trackEventWithName(eventName, options = {}) {
        if (skipsLegacyEvent(eventName)) {
            return;
        }
        this.pushEvent(eventName, options);
    }

    /**
     * This method pushes an event with a given name into the dataLayer.
     * @param {string} eventName - The name of the event to be tracked.
     * @param {Object} options - The options for the event tracking.
     * @param {Object} options.customData - The custom data for the event.
     * @param {boolean} options.addUtmData - Whether to add UTM data to the event.
     * @param {boolean} options.includePageData - Whether to include page data in the event.
     * @param {boolean} options.includeCampaignPageFlags - Whether to include is_campaign_page / is_young_couriers.
     * @param {Function} [options.eventCallback] - Invoked by GTM (once per loaded container) when all tags for this event have executed.
     * @param {number} [options.eventTimeout] - Milliseconds after which GTM invokes eventCallback even if tags are still pending.
     */
    pushEvent(
        eventName,
        { customData, addUtmData, includePageData, includeCampaignPageFlags, eventCallback, eventTimeout } = {},
    ) {
        this.executeTrackingEvent(() => {
            //default values
            addUtmData = addUtmData === undefined ? true : addUtmData;
            customData = customData === undefined ? {} : customData;
            includePageData = includePageData === undefined ? true : includePageData;
            includeCampaignPageFlags = includeCampaignPageFlags === undefined ? false : includeCampaignPageFlags;

            // Remove UTM data if consent was not given to analytics cookies
            if (!cookiesAccepted('analytics')) {
                addUtmData = false;
            }

            if ((includePageData || includeCampaignPageFlags) && !this.hasSetPageData) {
                this.setPageData();
            }

            let payload = { event: eventName };
            if (customData) payload = { ...customData, ...payload };

            if (includePageData) {
                // Read experiments at event time so Apply is not stuck with a missing assignment from the first pageview
                const experimentDetails = getActivatedExperiments();
                payload = {
                    ...this.pageData,
                    ...(experimentDetails ? { experiment_details: experimentDetails } : {}),
                    ...payload,
                };
            }

            if (includeCampaignPageFlags) {
                payload = {
                    is_campaign_page: sessionStorage.getItem('landing_is_campaign_page') === 'true',
                    is_young_couriers: sessionStorage.getItem('landing_is_young_couriers') === 'true',
                    ...payload,
                };
            }

            if (addUtmData) payload = { ...this.getUtmData(), ...payload };
            payload._clear = true;
            payload.utag_data = { tealium_event: 'custom_dev_none' };

            if (typeof eventCallback === 'function') {
                payload.eventCallback = eventCallback;
                if (eventTimeout !== undefined) {
                    payload.eventTimeout = eventTimeout;
                }
            }

            window.dataLayer.push(payload);
        });
    }

    /**
     * The number of GTM containers addScript() injects: the primary container,
     * plus the internal one when window.gtmAddOwnContainer is set.
     *
     * Not nice, but this is hardcoded. If we ever add more containers, we'll need to
     * update this function. Looks like there is no way to count containers
     * before they have finished loading.
     *
     * @returns {number} The container count.
     */
    loadedGtmContainerCount() {
        return window.gtmAddOwnContainer ? 2 : 1;
    }

    /**
     * This method tracks a legacy event and returns a Promise that resolves once GTM fired its tags,
     * see pushEventAwaited(). Resolves immediately in Dual model Employee + IC countries, where the legacy events are skipped.
     * @param {string} eventName - The name of the event to be tracked.
     * @param {Object} options - The options for the event tracking, see pushEvent().
     * @param {number} capMs - The hard cap in milliseconds.
     * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed.
     */
    trackEventWithNameAwaited(eventName, options = {}, capMs = 2000) {
        if (skipsLegacyEvent(eventName)) {
            return Promise.resolve();
        }
        return this.pushEventAwaited(eventName, options, capMs);
    }

    /**
     * This method pushes an event and returns a Promise that resolves once GTM reports that all
     * tags for the event have executed in every loaded container, or after `capMs`, whichever
     * comes first. It never rejects and never resolves later than ~`capMs`, so it is safe to
     * gate a redirect on it. Resolves immediately when the event cannot reach GTM right now
     * (tracking disabled, or cookie consent missing and the event only queued); the own cap
     * timer also covers GTM being blocked (e.g. ad-blocker), where eventCallback never fires.
     *
     * @param {string} eventName - The name of the event to be tracked.
     * @param {Object} options - The options for the event tracking, see pushEvent().
     * @param {number} capMs - The hard cap in milliseconds.
     * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed.
     */
    pushEventAwaited(eventName, options = {}, capMs = 2000) {
        return new Promise((resolve) => {
            let settled = false;
            const settle = () => {
                if (!settled) {
                    settled = true;
                    resolve();
                }
            };

            setTimeout(settle, capMs);

            if (!window.gtmTrackingEnabled || !this.hasCookieConsentForNewBanner()) {
                this.pushEvent(eventName, options);
                settle();
                return;
            }

            // eventCallback is invoked once per loaded container; wait for all of them
            let pendingContainers = this.loadedGtmContainerCount();
            this.pushEvent(eventName, {
                ...options,
                eventCallback: () => {
                    pendingContainers -= 1;
                    if (pendingContainers <= 0) {
                        settle();
                    }
                },
                eventTimeout: capMs,
            });
        });
    }

    /**
     * This method sets the page group.
     * @param {string} pageGroup - The page group to be set.
     */
    setPageGroup(pageGroup) {
        window.pageGroup = pageGroup;
        this.pageData.pageGroup = pageGroup;
    }

    /**
     * This method sets the page data.
     */
    setPageData() {
        let pageData = {
            homerunURL: window.location.href,
            url: window.location.pathname,
            country: window.country?.code ?? 'country_not_set',
            language: window.language?.code ?? 'language_not_set',
            'ut.env': this.formatEnvironment(window.gtmEnvironment ?? 'qa'),
        };

        appendRecruitmentLocationToPageData(pageData, {
            isInsideTrack: () => this.isInsideTrack(),
            isCityPage: () => this.isCityPage(),
            getCityValue: () => this.getCityValue(),
        });

        if (window.page === 'home_city') {
            try {
                sessionStorage.setItem('landing_is_campaign_page', window.isCampaignPage ? 'true' : 'false');
                sessionStorage.setItem('landing_is_young_couriers', window.isYoungCouriers ? 'true' : 'false');
            } catch (e) {
                // sessionStorage unavailable
            }
        }

        const pageGroup = resolveRecruitmentPageGroupForGtm(window.pageGroup);

        if (pageGroup) {
            pageData.pageGroup = pageGroup;
        }

        this.pageData = pageData;
        this.hasSetPageData = true;
    }

    /**
     * This method gets the UTM data.
     * @returns {Object} The UTM data.
     */
    getUtmData() {
        let utmData = {};
        // loop though all window variables that start with utm_ and add these to the object
        for (let key in window) {
            if (key.startsWith('utm_') && window[key]) {
                utmData[key] = window[key];
            }
        }
        return utmData;
    }

    /**
     * This method gets the interactive map value.
     * @returns {string} The interactive map value.
     */
    getInteractiveMapValue() {
        if (window.trackingMapComplete === undefined) {
            return undefined;
        }

        return window.trackingMapComplete ? 'complete' : 'incomplete';
    }

    /**
     * Track when a user has successfully loaded a screen/page
     */
    trackPageLoaded() {
        if (!this.hasSetPageData) {
            this.setPageData();
        }

        const pageId = uuidv4();
        const userAnonymousId = getUserId();

        const pageViewData = {
            pageGroup: this.pageData.pageGroup,

            page_id: pageId,
            country: this.pageData.country,
            'ut.env': this.pageData['ut.env'],
            language: this.pageData.language,

            user_anonymousId: userAnonymousId,

            original_referrer: this.originalReferrer,
            original_location: this.originalLocation,

            timestamp: new Date().toISOString(),
        };

        this.trackEventWithName('Pageview', {
            customData: pageViewData,
            addUtmData: false,
            includeCampaignPageFlags: true,
        });
    }

    /**
     * This method tracks a city apply form event.
     */
    trackCtaApplyForm(componentName = 'cta') {
        this.trackEventWithName('cta_apply_form', {
            customData: {
                component_name: componentName,
                component_type: 'cta',
                component_action: 'apply form',
            },
            addUtmData: true,
        });
    }

    /**
     * This method tracks a CTA apply event.
     */
    trackCtaApply() {
        this.trackEventWithName('cta_apply', {
            customData: {
                component_name: window.location.href,
                component_type: 'cta',
                component_action: 'cta_apply',
            },
        });
    }

    /**
     * This method tracks a has viewed form field event.
     * @param {string} name - The name of the field.
     */
    trackHasViewedFormField(name) {
        this.trackEventWithName('hasViewedFieldInForm', {
            customData: {
                fieldName: name,
            },
        });
    }

    /**
     * This method tracks a has filled form field event.
     * @param {string} name - The name of the field.
     */
    trackHasFilledFormField(name) {
        this.trackEventWithName('hasModifiedFieldInForm', {
            customData: {
                fieldName: name,
            },
        });
    }

    /**
     * This method tracks a form validation error event.
     * @param {string} component_name - The name of the component.
     * @param {string} error_message - The error message.
     * @param {string} form_info - The form information.
     */
    trackFormValidationError(component_name, error_message, form_info) {
        this.trackEventWithName('formField_error', {
            customData: {
                component_type: 'form',
                component_name: component_name,
                component_action: 'error',
                error_message: error_message,
            },
        });

        this.trackEventWithName('form_error', {
            customData: {
                component_type: 'form',
                component_name: form_info,
                component_action: 'error',
            },
        });
    }

    /**
     * This method tracks a has submitted form event.
     * The page redirects right after this event, so it is awaited: the returned promise lets the
     * caller delay the redirect until GTM fired the tags (e.g. Joveo conversion), capped at 2s.
     * @param {string} status - The status of the submission.
     * @param {string} applicantId - The ID of the applicant.
     * @param {string} applicationCity - The city of the application.
     * @param {string} hashed_email - The SHA-256 hashed email of the applicant.
     * @param {string} hashed_phone - The SHA-256 hashed phone number of the applicant.
     * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed.
     */
    trackHasSubmittedForm(status, applicantId, applicationCity, hashed_email, hashed_phone) {
        return this.trackEventWithNameAwaited('application_status', {
            customData: {
                component_name: 'application_status',
                component_type: 'application',
                component_action: status,
                application_city: applicationCity,
                applicantId: applicantId,
                interactive_map: this.getInteractiveMapValue(),
                hashed_email: hashed_email,
                hashed_phone: hashed_phone,
            },
            includeCampaignPageFlags: status === 'successfull',
        });
    }

    /**
     * This event is triggered when the user clicks on Apply now and no errors are triggered.
     * The page redirects right after this event, so it is awaited: the returned promise lets the
     * caller delay the redirect until GTM fired the tags (e.g. Joveo conversion), capped at 2s.
     * @param city - The city of the application.
     * @param applicantId - The ID of the applicant.
     * @param application - The application status.
     * @param hashed_email - The SHA-256 hashed email of the applicant.
     * @param hashed_phone - The SHA-256 hashed phone number of the applicant.
     * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed.
     */
    trackHasClickedApply(city, applicantId, application, hashed_email, hashed_phone, event_label) {
        return this.trackEventWithNameAwaited('hasClickedApply', {
            customData: {
                applicationCity: city,
                applicantId: applicantId,
                application: application,
                interactive_map: this.getInteractiveMapValue(),
                hashed_email: hashed_email,
                hashed_phone: hashed_phone,
                ...(event_label != null && { event_label }),
            },
            addUtmData: true,
        });
    }

    /**
     * This event is triggered when a user interacts with the salary slider
     * @param field - The name of the field that has been interacted with.
     * @param city_slider - The selected city.
     * @param hours_slider - The selected hours.
     * @param age_slider - The selected age.
     */
    trackHasInteractedWithSalarySlider(field, city_slider, hours_slider, age_slider) {
        this.trackEventWithName('slider_interaction', {
            customData: {
                component_type: 'salary_slider',
                component_name: field,
                component_action: 'edit',
                city_slider: city_slider,
                hours_slider: hours_slider,
                age_slider: age_slider,
            },
            addUtmData: true,
        });
    }

    /**
     * This event is triggered when a connection / server error occurs.
     * @param errorStatus - The error status.
     * @param errorMessage - The error message.
     * @param form_info - The form information.
     */
    trackHasNetworkError(errorStatus, errorMessage, form_info) {
        this.trackEventWithName('api_error', {
            customData: {
                component_type: 'form',
                component_action: 'error',
                component_name: form_info,
                error_code: errorStatus,
                error_message: errorMessage,
            },
            addUtmData: true,
        });
        this.trackEventWithName('form_error', {
            customData: {
                component_type: 'form',
                component_name: form_info,
                component_action: 'error',
            },
            addUtmData: true,
        });
    }

    /**
     * This event is triggered when a user clicks the icons on the map.
     * @param {'hub' | 'house' | 'remote'} iconName - The name of the icon.
     */
    trackHasClickedMapIcon(iconName) {
        this.trackEventWithName('map_click', {
            customData: {
                component_name: 'interactiveMap',
                component_type: 'map',
                component_action: 'click',
                city: this.getCityValue(),
                icon: iconName,
            },
            addUtmData: true,
        });
    }

    /**
     * This event is triggered when a user search a new city on the map.
     * @param {string} cityName - The name of the city that has been searched for.
     */
    trackHasSearchedCity(cityName) {
        this.trackEventWithName('interactiveMap_search', {
            customData: {
                component_name: 'interactiveMap',
                component_type: 'searchBox',
                component_action: 'search',
                city: cityName,
            },
            addUtmData: true,
        });
    }

    /**
     * This event is triggered when the user interacts with the map because of zoom in out.
     * @param {boolean} zoomedIn - True if the user zoomed in, false if the user zoomed out.
     */
    trackHasZoomedMap(zoomedIn = true) {
        this.trackEventWithName('map_interaction', {
            customData: {
                component_name: 'interactiveMap',
                component_type: 'map',
                component_action: zoomedIn ? 'zoom_in' : 'zoom_out',
            },
            addUtmData: true,
        });
    }

    /**
     * Track apply submit event, which is Delco specific
     * @param {string} componentType - The type of the component a user interacts with
     * @param {string} componentLocation - Where the component engaged with is positioned on the screen
     */
    trackApplySubmit(componentType, componentLocation) {
        const customData = {
            event_name: 'apply_submit',
            component_name: 'apply now',
            component_type: componentType,
            component_location: componentLocation,
        };

        if (typeof window !== 'undefined' && window.showHeroModal) {
            const raw = window.language?.hero_type;
            const heroType = typeof raw === 'string' ? raw : raw?.value ?? raw;
            if (heroType) {
                customData.hero_type = heroType;
            }
            customData.has_seen_hero_modal = 'yes';
        }

        this.trackEventWithName('apply_submit', {
            customData: customData,
            addUtmData: true,
            includePageData: true,
        });
    }

    /**
     * Track a click on a partner URL in the partners list (Poland EFC measurement plan)
     * @param {string} partnerName - The name of the clicked partner
     * @param {string} applicationCity - The name of the city selected in the partners city selector
     */
    trackPartnerLinkClick(partnerName, applicationCity) {
        this.trackEventWithName('partner_link_click', {
            customData: {
                event_name: 'partner_link_click',
                component_name: partnerName,
                component_type: 'button',
                application_city: applicationCity,
            },
            addUtmData: true,
            includePageData: true,
        });
    }

    /**
     * Track an experiment event
     * @param {string} eventName - The name of the analytics event to track
     * @param {Object} [options]
     * @param {Object} [options.customData] - Additional event-specific payload to merge into the event
     * @param {boolean} [options.addUtmData=true] - Whether to include UTM parameters
     */
    trackExperimentEvent(eventName, { customData = {}, addUtmData = true } = {}) {
        const activatedExp = getActivatedExperiments();

        this.trackEventWithName(eventName, {
            customData: {
                event_name: eventName,
                activatedExperiments: activatedExp,
                country: window.country?.code,
                pageType: window.currentCity ? 'city' : 'country',
                ...(customData || {}),
            },
            addUtmData,
        });
    }
}
